import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import usePermissions from '../../hooks/usePermissions';

const useStyles = makeStyles(theme => ({
  filtersContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterItem: {
    marginBottom: theme.spacing(1),
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  clearButton: {
    marginLeft: 'auto',
  },
}));

const MetricsFilters = ({ filters, onFiltersChange, onReset, onApply }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const profile = user?.profile;
  const { permissions } = usePermissions();

  const [localFilters, setLocalFilters] = useState(filters);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingQueues, setLoadingQueues] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    let isMounted = true;

    const fetchFilterOptions = async () => {
      try {
        // Buscar usuários (apenas para admin)
        if (permissions?.isAdmin) {
          if (isMounted) setLoadingUsers(true);
          try {
            // Tentar diferentes endpoints possíveis
            let response;
            try {
              response = await api.get('/users');
            } catch (error) {
              response = await api.get('/user');
            }

            const usersData = response.data;

            // Verificar diferentes estruturas possíveis
            let finalUsersData = usersData;
            if (usersData && usersData.users) {
              finalUsersData = usersData.users;
            } else if (usersData && usersData.data) {
              finalUsersData = usersData.data;
            }

            if (isMounted && Array.isArray(finalUsersData)) {
              setUsers(finalUsersData);
            } else if (isMounted) {
              setUsers([]);
            }
          } catch (error) {
            console.error('❌ Erro ao buscar usuários:', error);
            if (isMounted) setUsers([]);
          } finally {
            if (isMounted) setLoadingUsers(false);
          }
        }

        // Buscar filas
        if (isMounted) setLoadingQueues(true);
        try {
          // Tentar diferentes endpoints possíveis
          const response = await api.get('/queue');
          const queuesData = response.data;

          // Verificar diferentes estruturas possíveis
          let finalQueuesData = queuesData;
          if (queuesData && queuesData.queues) {
            finalQueuesData = queuesData.queues;
          } else if (queuesData && queuesData.data) {
            finalQueuesData = queuesData.data;
          }

          if (isMounted && Array.isArray(finalQueuesData)) {
            setQueues(finalQueuesData);
          } else if (isMounted) {
            setQueues([]);
          }
        } catch (error) {
          console.error('❌ Erro ao buscar filas:', error);
          if (isMounted) setQueues([]);
        } finally {
          if (isMounted) setLoadingQueues(false);
        }
      } catch (error) {
        console.error('Erro ao buscar opções de filtro:', error);
      }
    };

    fetchFilterOptions();

    return () => {
      isMounted = false;
    };
  }, [profile, user, permissions]);

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    if (onApply) onApply();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      userId: null,
      queueId: null,
      tagId: null,
      pipelineStage: null,
    };
    setLocalFilters(resetFilters);
    onReset(resetFilters);
  };

  const handleRefreshOptions = () => {
    // Recarregar todas as opções dos filtros
    const fetchFilterOptions = async () => {
      try {
        // Buscar usuários (apenas para admin)
        if (profile === 'admin') {
          setLoadingUsers(true);
          try {
            const { data: usersData } = await api.get('/users');
            if (Array.isArray(usersData)) {
              setUsers(usersData);
            } else {
              setUsers([]);
            }
          } catch (error) {
            console.error('Erro ao buscar usuários:', error);
            setUsers([]);
          } finally {
            setLoadingUsers(false);
          }
        }

        // Buscar filas
        setLoadingQueues(true);
        try {
          const { data: queuesData } = await api.get('/queue');
          if (Array.isArray(queuesData)) {
            setQueues(queuesData);
          } else {
            setQueues([]);
          }
        } catch (error) {
          console.error('Erro ao buscar filas:', error);
          setQueues([]);
        } finally {
          setLoadingQueues(false);
        }
      } catch (error) {
        console.error('Erro ao buscar opções de filtro:', error);
      }
    };

    fetchFilterOptions();
  };

  return (
    <Paper className={classes.filtersContainer} elevation={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label={i18n.t('metrics.filters.startDate')}
            type='date'
            value={localFilters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            className={classes.filterItem}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            label={i18n.t('metrics.filters.endDate')}
            type='date'
            value={localFilters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            className={classes.filterItem}
          />
        </Grid>

        {permissions?.isAdmin && (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth className={classes.filterItem}>
              <InputLabel>{i18n.t('metrics.filters.user')}</InputLabel>
              <Select
                value={localFilters.userId || ''}
                onChange={e =>
                  handleFilterChange('userId', e.target.value || null)
                }
                disabled={loadingUsers}
              >
                <MenuItem value=''>
                  {loadingUsers
                    ? i18n.t('common.loading')
                    : i18n.t('metrics.filters.allUsers')}
                </MenuItem>
                {users.map(user => (
                  <MenuItem
                    key={user.id || user._id}
                    value={user.id || user._id}
                  >
                    {user.name || user.userName || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth className={classes.filterItem}>
            <InputLabel>{i18n.t('metrics.filters.queue')}</InputLabel>
            <Select
              value={localFilters.queueId || ''}
              onChange={e =>
                handleFilterChange('queueId', e.target.value || null)
              }
              disabled={loadingQueues}
            >
              <MenuItem value=''>
                {loadingQueues
                  ? i18n.t('common.loading')
                  : i18n.t('metrics.filters.allQueues')}
              </MenuItem>
              {queues.map(queue => (
                <MenuItem
                  key={queue.id || queue._id}
                  value={queue.id || queue._id}
                >
                  {queue.name || queue.queueName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.actionsContainer}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<FilterIcon />}
              onClick={handleApplyFilters}
              className={classes.actionButton}
            >
              {i18n.t('metrics.filters.apply')}
            </Button>

            <IconButton
              onClick={handleRefreshOptions}
              title={i18n.t('metrics.filters.refresh')}
              className={classes.actionButton}
            >
              <RefreshIcon />
            </IconButton>

            <Button
              variant='outlined'
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
              className={classes.clearButton}
            >
              {i18n.t('metrics.filters.clear')}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MetricsFilters;
